// Fix #7 (2026-05-28): touch-based photo drag-and-drop for mobile.
//
// HTML5 dataTransfer fires only for mouse drags; touch events deliver no
// dataTransfer, so the existing onDragStart / onDrop pipeline is invisible
// on phones. This hook provides a parallel touch system:
//
//   1. onTouchStart on a photo thumbnail → start a 700ms hold timer.
//      If the finger moves > 8px before the timer fires we CANCEL — the
//      native scroll of the photo strip wins. If the timer fires while
//      the finger has stayed still we activate drag mode (haptic pulse),
//      create the floating clone, and bind move/end listeners on window.
//   2. touchmove (after activation) → reposition the clone. Use
//      document.elementFromPoint() to discover which slot the finger is
//      over and highlight it with a `.touch-drop-target-active` class.
//   3. touchend → read the slot under the finger one last time and fire
//      `onDrop(photoId, slotKey)`. The slot key is the same identifier
//      the desktop DnD pipeline uses, so the parent maps it through the
//      same handler.
//
// Slots opt in by adding `data-slot-drop-target="<slotKey>"` to their
// wrapper. AlbumEditorPage's PhotoSlot already does this (fix #15).
//
// Fix C (2026-05-28): without the hold-gate the strip was un-scrollable —
// touchstart immediately captured the gesture. 700ms / 8px threshold
// matches the @dnd-kit TouchSensor defaults used by SpreadStripSortable.

import { useCallback, useRef } from 'react';

const HOLD_MS = 700;
const MOVE_CANCEL_PX = 8;

type DragState = {
  photoId: string;
  clone: HTMLDivElement;
  lastTargetEl: HTMLElement | null;
};

type PendingState = {
  photoId: string;
  signedUrl: string | null;
  startX: number;
  startY: number;
  holdTimer: ReturnType<typeof setTimeout>;
};

export function useTouchDragPhoto(
  /** `at` (2026-10-20): where the finger lifted, as fractions of the target
   *  (0..1) — a cover face places the photo exactly there. Slots ignore it. */
  onDrop: (photoId: string, slotKey: string, at?: { fx: number; fy: number }) => void,
) {
  const stateRef = useRef<DragState | null>(null);
  const pendingRef = useRef<PendingState | null>(null);

  const clearHighlight = useCallback(() => {
    const last = stateRef.current?.lastTargetEl;
    if (last) last.classList.remove('touch-drop-target-active');
    if (stateRef.current) stateRef.current.lastTargetEl = null;
  }, []);

  const cleanup = useCallback(() => {
    clearHighlight();
    if (stateRef.current?.clone) {
      try { stateRef.current.clone.remove(); } catch { /* ignore */ }
    }
    stateRef.current = null;
    window.removeEventListener('touchmove', onTouchMoveRef.current as EventListener);
    window.removeEventListener('touchend', onTouchEndRef.current as EventListener);
    window.removeEventListener('touchcancel', onTouchEndRef.current as EventListener);
  }, [clearHighlight]);

  const clearPending = useCallback(() => {
    if (pendingRef.current) {
      clearTimeout(pendingRef.current.holdTimer);
      pendingRef.current = null;
    }
    window.removeEventListener('touchmove', onPendingMoveRef.current as EventListener);
    window.removeEventListener('touchend', onPendingEndRef.current as EventListener);
    window.removeEventListener('touchcancel', onPendingEndRef.current as EventListener);
  }, []);

  // We store the listeners in refs so the cleanup can detach the exact
  // bound function (anonymous closures would leak).
  const onTouchMoveRef = useRef<(ev: TouchEvent) => void>(() => undefined);
  const onTouchEndRef = useRef<(ev: TouchEvent) => void>(() => undefined);
  const onPendingMoveRef = useRef<(ev: TouchEvent) => void>(() => undefined);
  const onPendingEndRef = useRef<(ev: TouchEvent) => void>(() => undefined);

  onTouchMoveRef.current = (ev: TouchEvent) => {
    const st = stateRef.current;
    if (!st) return;
    const t = ev.touches[0];
    if (!t) return;
    // Prevent the page from scrolling while we drag.
    ev.preventDefault();
    st.clone.style.left = `${t.clientX}px`;
    st.clone.style.top = `${t.clientY}px`;

    // Temporarily hide the clone so elementFromPoint sees through it.
    st.clone.style.pointerEvents = 'none';
    st.clone.style.visibility = 'hidden';
    const under = document.elementFromPoint(t.clientX, t.clientY) as HTMLElement | null;
    st.clone.style.visibility = 'visible';

    // Walk up from whatever the finger landed on to the slot wrapper.
    // The slot wrapper carries the data-slot-drop-target attribute; its
    // inner <img> often intercepts elementFromPoint first.
    const slotEl = under?.closest('[data-slot-drop-target]') as HTMLElement | null;
    if (slotEl !== st.lastTargetEl) {
      if (st.lastTargetEl) st.lastTargetEl.classList.remove('touch-drop-target-active');
      if (slotEl) slotEl.classList.add('touch-drop-target-active');
      st.lastTargetEl = slotEl;
    }
  };

  onTouchEndRef.current = (ev: TouchEvent) => {
    const st = stateRef.current;
    if (!st) return;
    // Use changedTouches — touches is empty on touchend.
    const t = ev.changedTouches[0];
    let slotKey: string | null = null;
    let at: { fx: number; fy: number } | undefined;
    if (t) {
      st.clone.style.visibility = 'hidden';
      const under = document.elementFromPoint(t.clientX, t.clientY) as HTMLElement | null;
      const slotEl = under?.closest('[data-slot-drop-target]') as HTMLElement | null;
      slotKey = slotEl?.getAttribute('data-slot-drop-target') ?? null;
      if (slotEl) {
        const r = slotEl.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          at = {
            fx: Math.max(0, Math.min(1, (t.clientX - r.left) / r.width)),
            fy: Math.max(0, Math.min(1, (t.clientY - r.top) / r.height)),
          };
        }
      }
    }
    const photoId = st.photoId;
    cleanup();
    if (slotKey) {
      onDrop(photoId, slotKey, at);
    }
  };

  // Activate drag — called either after the hold timer fires OR by an
  // explicit caller (not currently used externally).
  const activateDrag = useCallback((photoId: string, signedUrl: string | null, x: number, y: number) => {
    const clone = document.createElement('div');
    clone.setAttribute('aria-hidden', 'true');
    Object.assign(clone.style, {
      position: 'fixed',
      left: `${x}px`,
      top: `${y}px`,
      width: '72px',
      height: '72px',
      marginLeft: '-36px',
      marginTop: '-36px',
      borderRadius: '12px',
      background: '#eee',
      backgroundImage: signedUrl ? `url(${JSON.stringify(signedUrl)})` : '',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      boxShadow: '0 8px 24px rgba(0,0,0,0.25), 0 0 0 2px #C9A24A',
      zIndex: '99999',
      pointerEvents: 'none',
      transform: 'scale(1.08)',
      transition: 'transform 120ms ease',
    } as Partial<CSSStyleDeclaration>);
    document.body.appendChild(clone);

    stateRef.current = { photoId, clone, lastTargetEl: null };

    // Light haptic to signal "you are now dragging."
    try { navigator.vibrate?.(50); } catch { /* ignore — older Safari */ }

    window.addEventListener('touchmove', onTouchMoveRef.current as EventListener, { passive: false });
    window.addEventListener('touchend', onTouchEndRef.current as EventListener);
    window.addEventListener('touchcancel', onTouchEndRef.current as EventListener);
  }, []);

  // Pending phase — listens for finger excursion to cancel the hold.
  onPendingMoveRef.current = (ev: TouchEvent) => {
    const pending = pendingRef.current;
    if (!pending) return;
    const t = ev.touches[0];
    if (!t) return;
    const dx = t.clientX - pending.startX;
    const dy = t.clientY - pending.startY;
    if (Math.hypot(dx, dy) > MOVE_CANCEL_PX) {
      // Finger moved before the hold completed — bail out and let the
      // native scroll of the photo strip take the gesture.
      clearPending();
    }
  };

  onPendingEndRef.current = () => {
    // Released before the hold fired — never a drag.
    clearPending();
  };

  const beginTouchDrag = useCallback(
    (photoId: string, signedUrl: string | null, ev: React.TouchEvent) => {
      const t = ev.touches[0];
      if (!t) return;
      // Cancel any previous pending hold before starting a new one.
      clearPending();

      const startX = t.clientX;
      const startY = t.clientY;

      const holdTimer = setTimeout(() => {
        // Hold completed. Stop watching for cancel, start the drag.
        window.removeEventListener('touchmove', onPendingMoveRef.current as EventListener);
        window.removeEventListener('touchend', onPendingEndRef.current as EventListener);
        window.removeEventListener('touchcancel', onPendingEndRef.current as EventListener);
        pendingRef.current = null;
        activateDrag(photoId, signedUrl, startX, startY);
      }, HOLD_MS);

      pendingRef.current = { photoId, signedUrl, startX, startY, holdTimer };

      // While pending we use passive listeners — we MUST NOT preventDefault
      // because that would block the native scroll the user is trying to do.
      window.addEventListener('touchmove', onPendingMoveRef.current as EventListener, { passive: true });
      window.addEventListener('touchend', onPendingEndRef.current as EventListener);
      window.addEventListener('touchcancel', onPendingEndRef.current as EventListener);
    },
    [activateDrag, clearPending],
  );

  return { beginTouchDrag };
}
